import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Info, Calendar, Percent } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DEFAULT_VACATION_PERCENTAGE_4W } from '@/config/constants';

interface VacationSettingsProps {
  vacationModel: 'fixed_days' | 'percentage_included';
  annualDays?: number;
  percentageRate?: number;
  onVacationModelChange: (model: 'fixed_days' | 'percentage_included') => void;
  onAnnualDaysChange: (days: number) => void;
  onPercentageRateChange: (rate: number) => void;
  className?: string;
}

export const VacationSettings: React.FC<VacationSettingsProps> = ({
  vacationModel,
  annualDays = 20,
  percentageRate = DEFAULT_VACATION_PERCENTAGE_4W,
  onVacationModelChange,
  onAnnualDaysChange,
  onPercentageRateChange,
  className
}) => {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Ferien-Modell
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <TooltipProvider>
          <RadioGroup
            value={vacationModel}
            onValueChange={(value) => onVacationModelChange(value as 'fixed_days' | 'percentage_included')}
            className="space-y-4"
          >
            {/* Fixed Days Model */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fixed_days" id="fixed_days" />
                <Label htmlFor="fixed_days" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Feste Ferientage pro Jahr
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Ferientage werden separat verwaltet und von Arbeitstagen abgezogen. Geeignet für Monatslöhner und feste Anstellungen.</p>
                    </TooltipContent>
                  </Tooltip>
                </Label>
              </div>

              {vacationModel === 'fixed_days' && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="annual_days" className="text-sm">
                    Ferientage pro Jahr
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="annual_days"
                      type="number"
                      min="0"
                      max="50"
                      value={annualDays}
                      onChange={(e) => onAnnualDaysChange(parseInt(e.target.value) || 0)}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">Tage</span>
                    <Badge variant="outline" className="text-xs">
                      Standard: 20-25 Tage
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ferientage werden verdient und können bezogen werden. 
                    Sichtbar in Lohnabrechnung und Jahresberichten.
                  </p>
                </div>
              )}
            </div>

            {/* Percentage Included Model */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="percentage_included" id="percentage_included" />
                <Label htmlFor="percentage_included" className="flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  Ferienentschädigung im Stundenlohn
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Ferienentschädigung ist bereits im Stundenlohn enthalten. Üblich bei Stundenlöhnern und temporären Anstellungen.</p>
                    </TooltipContent>
                  </Tooltip>
                </Label>
              </div>

              {vacationModel === 'percentage_included' && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="percentage_rate" className="text-sm">
                    Ferienentschädigung
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="percentage_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="20"
                      value={percentageRate}
                      onChange={(e) => onPercentageRateChange(parseFloat(e.target.value) || 0)}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    <Badge variant="outline" className="text-xs">
                      Standard: 8.33% (4 Wochen) oder 10.64% (5 Wochen)
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    8.33% entspricht 4 Wochen Ferien, 10.64% entspricht 5 Wochen Ferien pro Jahr.
                    Wird bei jeder Lohnzahlung ausbezahlt.
                  </p>
                </div>
              )}
            </div>
          </RadioGroup>
        </TooltipProvider>

        {/* Explanation Card */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-sm mb-2">Unterschied der Modelle</h4>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div>
              <strong>Feste Ferientage:</strong> Ferien werden als separate Tage verwaltet, 
              verdient und bezogen. Üblich bei festen Anstellungen.
            </div>
            <div>
              <strong>Ferienentschädigung:</strong> Ferienentschädigung wird bei jeder 
              Lohnzahlung direkt ausbezahlt. Üblich bei Stundenlöhnern.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};